/**
 * Admin · Status history — full workspace timeline of every status change.
 *
 * Hits `GET /api/admin/workspaces/{ws}/status-history` with optional
 * `status_filter` and `group_filter`. Calm vocabulary; no surveillance words.
 */
import { useEffect, useMemo, useState } from "react";
import { Filter, Sparkles, Download } from "lucide-react";
import { api } from "../../lib/api";
import { useAdminWorkspace, AdminHeader, AdminEmpty } from "./_shared";

const STATUS_LABEL = {
  yard: "In the yard",
  play: "At play",
  eat: "Sharing a meal",
  rest: "Resting",
  in_care: "In care",
  arrived: "Just arrived",
  checked_out: "Checked out",
  absent: "Resting at home",
  sick: "Resting (unwell)",
  vacation: "On holiday",
};

const STATUS_OPTIONS = [
  "yard", "play", "eat", "rest", "in_care",
  "arrived", "checked_out", "absent", "sick", "vacation",
];

const SOURCE_LABEL = {
  parent_app: "parent",
  staff_dashboard: "staff",
  child_interface: "kiosk",
  admin_panel: "admin",
  scheduler: "room",
};

const BUCKET_COLOR = {
  fresh: "#1A1B2E",
  aging: "#D4A85F",
  needs_confirmation: "#9CA3AF",
  not_applicable: "#1F3A2A",
};

function bucketFor(status, atIso) {
  if (["checked_out", "absent", "sick", "vacation"].includes(status))
    return "not_applicable";
  if (!atIso) return "needs_confirmation";
  const sec = (Date.now() - new Date(atIso).getTime()) / 1000;
  if (sec < 600) return "fresh";
  if (sec < 1500) return "aging";
  return "needs_confirmation";
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function AdminStatusHistory() {
  const { workspace, loading: wsLoading } = useAdminWorkspace();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [groupFilter, setGroupFilter] = useState("");

  useEffect(() => {
    if (!workspace) return;
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams({ days: String(days), limit: "500" });
    if (statusFilter) params.set("status_filter", statusFilter);
    if (groupFilter) params.set("group_filter", groupFilter);
    api
      .get(
        `/admin/workspaces/${workspace.id}/status-history?${params.toString()}`
      )
      .then((r) => alive && setRows(r.data || []))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [workspace?.id, days, statusFilter, groupFilter]);

  const groups = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.group_id && s.add(r.group_id));
    return [...s].sort();
  }, [rows]);

  if (wsLoading) return null;
  if (!workspace)
    return (
      <AdminEmpty testid="admin-status-history-no-ws">
        No workspace yet.
      </AdminEmpty>
    );

  const downloadCsv = async () => {
    const params = new URLSearchParams({ days: String(days) });
    if (statusFilter) params.set("status_filter", statusFilter);
    if (groupFilter) params.set("group_filter", groupFilter);
    try {
      const res = await api.get(
        `/admin/workspaces/${workspace.id}/status-history.csv?${params.toString()}`,
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `alouvya-status-history-${workspace.name.replace(/\s+/g, "-")}-${days}d.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // quiet — keep the panel calm even if the download misfires
    }
  };

  return (
    <div data-testid="admin-status-history-screen">
      <AdminHeader
        eyebrow="Status history"
        title="Every gentle update, in order"
      />
      <div className="mt-2 text-sm text-[#525252] max-w-2xl">
        A calm, append-only timeline of every status change across the room.
        Human-updated by your team. Never a camera, never a sensor.
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2 bg-white border hairline rounded-full px-4 py-2">
          <Filter size={13} strokeWidth={1.6} className="text-[#525252]" />
          <label className="text-[11px] tracking-[0.18em] uppercase text-[#525252]">
            Days
          </label>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            data-testid="admin-status-history-days"
            className="bg-transparent outline-none text-sm font-medium"
          >
            {[1, 3, 7, 14, 30, 90].map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <div className="inline-flex items-center gap-2 bg-white border hairline rounded-full px-4 py-2">
          <label className="text-[11px] tracking-[0.18em] uppercase text-[#525252]">
            Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="admin-status-history-status-filter"
            className="bg-transparent outline-none text-sm font-medium"
          >
            <option value="">All</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s] || s}
              </option>
            ))}
          </select>
        </div>
        {groups.length > 0 && (
          <div className="inline-flex items-center gap-2 bg-white border hairline rounded-full px-4 py-2">
            <label className="text-[11px] tracking-[0.18em] uppercase text-[#525252]">
              Group
            </label>
            <select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              data-testid="admin-status-history-group-filter"
              className="bg-transparent outline-none text-sm font-medium"
            >
              <option value="">All groups</option>
              {groups.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </div>
        )}
        <button
          type="button"
          onClick={downloadCsv}
          data-testid="admin-status-history-download-csv"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#132619] text-white text-xs tracking-[0.16em] uppercase font-medium hover:bg-[#0b2117] transition-colors"
        >
          <Download size={13} strokeWidth={1.7} />
          Download CSV
        </button>
      </div>

      {loading ? (
        <AdminEmpty testid="admin-status-history-loading">
          A quiet moment…
        </AdminEmpty>
      ) : rows.length === 0 ? (
        <AdminEmpty testid="admin-status-history-empty">
          Nothing to gently recount in this range.
        </AdminEmpty>
      ) : (
        <ul
          data-testid="admin-status-history-list"
          className="mt-6 rounded-3xl bg-white border hairline overflow-hidden divide-y divide-[#171717]/6"
        >
          {rows.map((r) => {
            const bucket = bucketFor(r.status, r.at);
            return (
              <li
                key={r.id}
                data-testid={`admin-status-history-row-${r.id}`}
                className="px-5 py-4 flex items-start gap-4"
              >
                <span
                  aria-hidden
                  className="h-9 w-9 rounded-2xl bg-[#F2EFEA] flex items-center justify-center shrink-0 mt-0.5"
                >
                  <Sparkles size={14} strokeWidth={1.6} className="text-[#1A1B2E]" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline flex-wrap gap-2">
                    <span className="font-serif text-base text-[#171717]">
                      {r.child_name}
                    </span>
                    <span className="text-sm text-[#525252] font-medium">
                      · {STATUS_LABEL[r.status] || r.status}
                    </span>
                    {r.group_id && (
                      <span className="text-[10px] tracking-[0.18em] uppercase text-[#525252]">
                        · {r.group_id}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#525252] mt-1">
                    {r.updated_by_user_name} · {r.updated_by_role}
                    {" "}
                    <span className="text-[#9CA3AF]">
                      via {SOURCE_LABEL[r.source] || r.source}
                    </span>
                  </div>
                  {r.note && (
                    <div className="text-xs text-[#525252] mt-1 italic">
                      “{r.note}”
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className="text-[10px] tracking-[0.18em] uppercase text-[#525252]">
                    {fmt(r.at)}
                  </span>
                  <span
                    aria-hidden
                    className="h-2 w-2 rounded-full"
                    style={{ background: BUCKET_COLOR[bucket] }}
                    title={bucket.replace("_", " ")}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
